import { ApolloCache } from "@apollo/client";
import {
    GetInQueueScenariosDocument,
    GetInQueueScenariosQuery,
    InQueueScenarioFieldsFragment,
    Scenarios_Insert_Input,
    Scenario_Origins_Enum,
    useCreateScenariosMutation,
    useUpdateScenarioMutation
} from "generated/graphql";
import { useAllQueueProjects } from "in_queue/contexts/ProjectDataContext";
import { Cluster } from "in_queue/types/clusterType";
import {
    ProjectSize,
    QueueProject,
    SpeculativeProject,
    TrackedProject
} from "in_queue/types/projectType";
import {
    CostAssumption,
    CUSTOM_UPGRADE_ID_KEY,
    MisoPercentageReduction,
    ScenarioMetadata,
    ScenarioStage,
    ScenarioStatus
} from "in_queue/types/scenarioType";
import { isProjectExcluded } from "in_queue/types/scenarioUtils";
import { useMemo } from "react";
import { User } from "types/userType";
import { stageToDefaultCostAssumption } from "../ScenarioDialog/CostAssumptionsPanel";
import { getScenarioClusterFields } from "../ScenarioDialog/scenarioClusterFields";
import { dropPartialWholeProjectOnlyAssumptions } from "../ScenarioDialog/wholeProjectOnly";

export const SCENARIO_COPY_SUFFIX = " copy";

export const getScenarioDialogConfigFromExistingScenario = ({
    projectId,
    scenario,
    action,
    scenarioStage,
    validProjectIds,
    wholeProjectOnlyProjectIds = new Set<string>()
}: {
    projectId?: string;
    scenario?: ScenarioMetadata;
    action: "create" | "update";
    scenarioStage: ScenarioStage;
    validProjectIds: string[];
    // Projects the ISO studies whole. A saved scenario predating that rule can
    // carry a partial reduction on one; it is dropped here rather than copied
    // forward, because the pipeline rejects it on the run's first asset.
    wholeProjectOnlyProjectIds?: Set<string>;
}): {
    title: string;
    scenarioStage: ScenarioStage;
    costAssumptions: CostAssumption;
    projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    includedSpeculativeProjectIds: string[];
    parentProjectId: string | undefined;
    customUpgradeId: string | null;
    droppedWholeProjectOnlyIds: string[];
} => {
    const useExistingScenario = projectId && scenario;

    if (!projectId || !useExistingScenario) {
        return {
            title: "",
            projectSizeAssumptions: {},
            scenarioStage,
            costAssumptions: stageToDefaultCostAssumption[scenarioStage],
            includedSpeculativeProjectIds: [],
            parentProjectId: undefined,
            customUpgradeId: null,
            droppedWholeProjectOnlyIds: []
        };
    }

    const copiedAssumptions: Record<string, MisoPercentageReduction> = {};
    Object.keys(scenario.assumptions).forEach((projectId: string) => {
        // we need to make sure that the project is still a editable/valid project before copying
        // it over to a new scenario
        if (validProjectIds.includes(projectId)) {
            copiedAssumptions[projectId] = scenario.assumptions[projectId];
        }
    });
    const { assumptions: projectSizeAssumptions, droppedProjectIds } =
        dropPartialWholeProjectOnlyAssumptions(
            copiedAssumptions,
            wholeProjectOnlyProjectIds
        );
    const title =
        action === "create"
            ? `${scenario.title}${SCENARIO_COPY_SUFFIX}`
            : `${scenario.title}`;

    return {
        title,
        projectSizeAssumptions,
        scenarioStage: scenario.stage,
        costAssumptions: scenario.costAssumptions,
        includedSpeculativeProjectIds: scenario.projectAdditions.map(
            (pa) => pa.projectId
        ),
        parentProjectId: scenario.parentProjectId,
        customUpgradeId: scenario.customUpgradeId ?? null,
        droppedWholeProjectOnlyIds: droppedProjectIds
    };
};

interface CreateOrUpdateScenarioResponse {
    scenario_id: string;
    title: string;
}

interface UpdateScenarioProps {
    scenarioId: string;
    auth0Id: string;
    title: string;
    runOnSubmit: boolean;
    projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    parentProjectId?: string;
    parentSpeculativeProjectId?: string;
    costAssumptions: CostAssumption;
    includedSpeculativeProjects: SpeculativeProject[];
    customUpgradeId: string | null;
}

export const useUpdateScenario: () => (
    props: UpdateScenarioProps
) => Promise<CreateOrUpdateScenarioResponse | undefined> = () => {
    const [updateScenarioMutation] = useUpdateScenarioMutation();

    const updateScenario = async ({
        scenarioId,
        auth0Id,
        title,
        runOnSubmit,
        projectSizeAssumptions,
        parentProjectId,
        parentSpeculativeProjectId,
        costAssumptions,
        includedSpeculativeProjects,
        customUpgradeId
    }: UpdateScenarioProps) => {
        const response = await updateScenarioMutation({
            variables: {
                scenarioId: scenarioId,
                authId: auth0Id,
                title,
                status: runOnSubmit
                    ? ScenarioStatus.RunRequested
                    : ScenarioStatus.Draft,
                projectSizeAssumptions,
                projectAdditions: formatProjectAdditionsForQuery(
                    includedSpeculativeProjects
                ),
                customUpgrades: formatCustomUpgradesForQuery(customUpgradeId),
                parentProjectId,
                parentSpeculativeProjectId,
                costAssumptions
            }
        });
        if (
            response.data &&
            response.data.update_scenarios &&
            response.data.update_scenarios.affected_rows === 0
        ) {
            throw new Error("Unable to update scenario");
        }
        return response.data?.update_scenarios?.returning[0];
    };

    return updateScenario;
};

interface CreateScenarioProps {
    cluster: Cluster;
    project: TrackedProject;
    userData: User;
    title: string;
    runOnSubmit: boolean;
    projectSizeAssumptions: Record<string, MisoPercentageReduction>;
    scenarioStage: ScenarioStage;
    parentProjectId?: string;
    parentSpeculativeProjectId?: string;
    costAssumptions: CostAssumption;
    includedSpeculativeProjects: SpeculativeProject[];
    customUpgradeId: string | null;
}

const scenarioInsertInput = ({
    cluster,
    project,
    userData,
    title,
    runOnSubmit,
    projectSizeAssumptions,
    scenarioStage,
    parentProjectId,
    parentSpeculativeProjectId,
    costAssumptions,
    includedSpeculativeProjects,
    customUpgradeId
}: CreateScenarioProps): Scenarios_Insert_Input => {
    const { region, studyGroup, studyCycle, studyPhase } =
        getScenarioClusterFields(cluster);
    return {
        scenario_type: "CUSTOM",
        scenario_origin: Scenario_Origins_Enum.Custom,
        scenario_stage: scenarioStage,
        region,
        study_group: studyGroup,
        study_cycle: studyCycle,
        study_phase: studyPhase,
        team_id: userData.teamId,
        created_by: userData.auth0Id,
        updated_by: userData.auth0Id,
        title: title || "New scenario",
        project_size_assumptions: projectSizeAssumptions,
        project_additions: formatProjectAdditionsForQuery(
            includedSpeculativeProjects
        ),
        custom_upgrades: formatCustomUpgradesForQuery(customUpgradeId),
        status: runOnSubmit
            ? ScenarioStatus.RunRequested
            : ScenarioStatus.Draft,
        subscription_id: project.subscriptionId,
        parent_project_id: parentProjectId,
        parent_speculative_project_id: parentSpeculativeProjectId,
        cost_assumptions: costAssumptions
    };
};

/**
 * Append freshly-created scenarios to the cached scenarios list so they show
 * up before the subscription next pushes. Skips any the cache already has.
 */
const addCreatedScenariosToCache = (
    cache: ApolloCache<unknown>,
    created: ReadonlyArray<InQueueScenarioFieldsFragment>
): void => {
    if (created.length === 0) {
        return;
    }
    try {
        cache.updateQuery<GetInQueueScenariosQuery>(
            { query: GetInQueueScenariosDocument },
            (existing) => {
                if (!existing) {
                    return undefined;
                }
                const cachedIds = new Set(
                    existing.scenarios.map((s) => s.scenario_id)
                );
                const missing = created.filter(
                    (s) => !cachedIds.has(s.scenario_id)
                );
                return missing.length === 0
                    ? undefined
                    : {
                          ...existing,
                          scenarios: [...existing.scenarios, ...missing]
                      };
            }
        );
    } catch (error) {
        console.warn(
            `Failed to add scenarios ${created
                .map((s) => s.scenario_id)
                .join(
                    ", "
                )} to the cached scenarios list. They will appear once the subscription next updates.`,
            error
        );
    }
};

export interface CreateScenariosResult {
    created: ReadonlyArray<InQueueScenarioFieldsFragment>;
    archivedCount: number;
}

/**
 * Create scenarios, optionally archiving the ones they were copied from.
 *
 * The archive rides along in the same transaction rather than a second
 * mutation. Two commits make the scenarios list flicker: the insert's
 * live-query push is computed before the archive commits, so it replaces the
 * cached list with a snapshot where the originals are still unarchived.
 */
export const useCreateScenarios: () => (
    props: ReadonlyArray<CreateScenarioProps>,
    originalScenarioIdsToArchive?: ReadonlyArray<string>
) => Promise<CreateScenariosResult> = () => {
    const [createScenariosMutation] = useCreateScenariosMutation();

    const createScenarios = async (
        props: ReadonlyArray<CreateScenarioProps>,
        originalScenarioIdsToArchive: ReadonlyArray<string> = []
    ) => {
        if (props.length === 0) {
            return { created: [], archivedCount: 0 };
        }
        const response = await createScenariosMutation({
            update: (cache, { data }) =>
                addCreatedScenariosToCache(
                    cache,
                    data?.insert_scenarios?.returning ?? []
                ),
            variables: {
                objects: props.map(scenarioInsertInput),
                archiveOriginals: originalScenarioIdsToArchive.length > 0,
                originalScenarioIds: originalScenarioIdsToArchive,
                lastArchivedAt: new Date().toISOString()
            }
        });
        return {
            created: response.data?.insert_scenarios?.returning ?? [],
            archivedCount: response.data?.update_scenarios?.affected_rows ?? 0
        };
    };

    return createScenarios;
};

export const useCreateScenario: () => (
    props: CreateScenarioProps
) => Promise<CreateOrUpdateScenarioResponse | undefined> = () => {
    const createScenarios = useCreateScenarios();

    return async (props: CreateScenarioProps) =>
        (await createScenarios([props])).created[0];
};

const formatProjectAdditionsForQuery = (
    speculativeProjects: SpeculativeProject[]
) =>
    speculativeProjects.map((sp) => ({
        speculative_project_id: sp.id
    }));

const formatCustomUpgradesForQuery = (customUpgradeId: string | null) =>
    customUpgradeId !== null
        ? [{ [CUSTOM_UPGRADE_ID_KEY]: customUpgradeId }]
        : [];

export interface ProjectSizeAssumptionsMetadata {
    droppedProjectCount: number;
    downsizedProjectCount: number;
    mwRemoved: number;
    clusterMw: number;
    percentageMwRemoved: number;
}

const EMPTY_PROJECT_SIZE_ASSUMPTIONS_METADATA: ProjectSizeAssumptionsMetadata =
    {
        droppedProjectCount: 0,
        downsizedProjectCount: 0,
        mwRemoved: 0,
        clusterMw: 0,
        percentageMwRemoved: 0
    };

export const getProjectSizeAssumptionsMetadata = (
    clusterProjects: ReadonlyArray<QueueProject>,
    projectSizeAssumptions: Record<string, MisoPercentageReduction>
): ProjectSizeAssumptionsMetadata => {
    let droppedProjectCount = 0;
    let downsizedProjectCount = 0;
    let mwRemoved = 0;

    const projectIdToSize: Record<string, ProjectSize> = {};
    let clusterMw = 0;

    // the ERIS and NRIS sizes could technically be different, but we default to the ERIS size for simplicity
    clusterProjects.forEach((project: QueueProject) => {
        clusterMw += project.size.erisMw;
        projectIdToSize[project.projectId] = project.size;
    });

    for (const [assumptionProjectId, assumption] of Object.entries(
        projectSizeAssumptions
    )) {
        if (!assumption) {
            continue;
        }
        if (isProjectExcluded(assumption)) {
            droppedProjectCount += 1;
        } else {
            downsizedProjectCount += 1;
        }

        // In rare cases a project might be removed from the all_projects table and that would cause this to
        // break. That should never happen, but adding these here just in case
        if (assumptionProjectId in projectIdToSize) {
            mwRemoved +=
                projectIdToSize[assumptionProjectId].erisMw *
                (assumption.eris / 100);
        }
    }

    return {
        droppedProjectCount,
        downsizedProjectCount,
        mwRemoved,
        clusterMw,
        percentageMwRemoved: mwRemoved / clusterMw
    };
};

export const useProjectSizeAssumptionsMetadata = ({
    projectSizeAssumptions
}: {
    projectSizeAssumptions: Record<string, MisoPercentageReduction>;
}): ProjectSizeAssumptionsMetadata => {
    const clusterProjects = useAllQueueProjects();

    return useMemo(
        () =>
            clusterProjects === "loading"
                ? EMPTY_PROJECT_SIZE_ASSUMPTIONS_METADATA
                : getProjectSizeAssumptionsMetadata(
                      clusterProjects,
                      projectSizeAssumptions
                  ),
        [clusterProjects, projectSizeAssumptions]
    );
};
